// --------- Component for the alternative data page of the application -------------
// ----------------------------------------------------------------------------------

// import react and all components
import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import Sidebar from './Sidebar';
import NewsPanel from './NewsPanel';
import GoogleTrendsPanel from './GoogleTrendsPanel';
import PanelWrapper from './PanelWrapper';
import './HomePage.css'; // import same styling as the dashboard page

const AlternativeData = () => { // create the alternative data page component
  // set all states for the page
  const [timeframe, setTimeframe] = useState('2015-01-01|2025-12-31');  // timeframe state
  const [showCustomPicker, setShowCustomPicker] = useState(false); // state to control visibility of custom date picker
  const [customStartDate, setCustomStartDate] = useState('2020-01-01'); // state to hold the custom start date, defaulting to a recent date within the range
  const [customEndDate, setCustomEndDate] = useState('2024-12-31'); // state to hold the custom end date, defaulting to a recent date within the range

  // states to hold years for the top buttons
  const years = ['2015','2016','2017','2018','2019','2020','2021','2022','2023','2024','2025'];
  const fullRange = '2015-01-01|2025-12-31';

  // function to handle applying the custom date range
  const handleCustomDateApply = () => {
    setTimeframe(`${customStartDate}|${customEndDate}`); // set the timeframe to the custom range in the format expected by the API
    setShowCustomPicker(false); // hide the custom date picker after applying the range
  };

  // determine if the current timeframe is a custom range or not, and create a label for the custom button if it is
  const isCustomTimeframe = timeframe && timeframe.includes('|') && timeframe !== fullRange;

  let displayCustomLabel; // declare the variable to hold the label for the custom button

  if (isCustomTimeframe) {
    // pull apart the timeframe string into a start date and end date
    const parts = timeframe.split('|');   
    const startDate = parts[0];           
    const endDate = parts[1];             

    displayCustomLabel = startDate + ' → ' + endDate; // create a label for the custom button in the format "start date → end date"
  } else {

    displayCustomLabel = 'Custom';
  }

  return ( // return the JSX
    <div className="dashboard"> {/* main container for the page */}
    
      <Sidebar timeframe={timeframe} /> {/* include the sidebar */}

      <div className="main-content"> {/* main content area for the page*/}

        {/* begin the header section of the page */}
        <div className="alt-data-header">

          <h2>Alternative Data Analysis</h2> {/* header name for the page */}

          <div className="timeframe-selector-alt"> {/* section for the timeframe selection buttons */}

            {/* All button — full 2015–2025 range */}
            <button
              className={timeframe === fullRange ? 'active' : ''}
              onClick={() => setTimeframe(fullRange)}
            >
              All
            </button>

            {/* Year buttons — individual years from 2015 to 2025 */}
            {years.map(yr => ( 
              <button
                key={yr} // set the button active if the timeframe matches the year, otherwise leave it inactive
                className={timeframe === yr ? 'active' : ''}
                onClick={() => setTimeframe(yr)}
              >
                {yr}
              </button>
            ))}

            {/* Custom Date Range Button */}
            <div className="custom-date-container-alt">
              <button
                className={`custom-date-btn-alt ${isCustomTimeframe ? 'active' : ''}`} // activate the button if pressed and the timeframe is a custom range
                onClick={() => setShowCustomPicker(!showCustomPicker)} // on the click of the button, toggle the visibility of the custom date picker - opposite of what it currently is
              >
                <Calendar size={14} /> {/* calendar icon for the custom date button */}
                {isCustomTimeframe ? displayCustomLabel : 'Custom'}
                <ChevronDown size={14} className={showCustomPicker ? 'rotated' : ''} />
              </button>

              {showCustomPicker && ( // show the custom date picker if the custom date button is clicked
                <div className="custom-date-picker-alt">  
                  <div className="date-input-group">
                    <label>Start Date:</label>
                    <input
                      type="date"
                      value={customStartDate}
                      min="2015-01-01"
                      max="2025-12-31"
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>

                  <div className="date-input-group">
                    <label>End Date:</label>
                    <input
                      type="date"
                      value={customEndDate}
                      min="2015-01-01"
                      max="2025-12-31"
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>

                  <div className="date-picker-actions">
                    <button
                      className="apply-btn"
                      onClick={handleCustomDateApply}
                      disabled={customStartDate >= customEndDate}
                    >
                      Apply
                    </button>

                    <button
                      className="cancel-btn"
                      onClick={() => setShowCustomPicker(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* final section to actually hold the data */}
        <div className="alternative-data-grid">
          
          <PanelWrapper id="trends" className="alt-trends-section">
            <GoogleTrendsPanel timeframe={timeframe} /> {/* include the google trends panel */}
          </PanelWrapper>

          <PanelWrapper id="news" className="alt-news-section">
            <NewsPanel timeframe={timeframe} /> {/* include the news panel */}
          </PanelWrapper>

        </div>
      </div>
    </div>
  );
};

export default AlternativeData; // return component to be used in app.js

